import {
  formatPhone,
  type InviteLinkCheck,
  type SyncFamily,
  type SyncGroup,
  type SyncPerson,
  type SyncResult,
  type SyncRole,
  type SyncSwimmer,
  type WhatsAppSync,
} from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Avatar,
  Badge,
  Button,
  CellField,
  Checkbox,
  Combobox,
  ConfirmDialog,
  DataTable,
  InlineError,
  Field,
  Notice,
  Panel,
  RadioGroup,
  SkeletonList,
  Tabs,
  showToast,
  type ComboboxOption,
  type DataTableColumn,
} from "../../components/index.ts";
import { isApiError, newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { formatDate, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useArchivePreview,
  useArchiveSyncPerson,
  useCheckInviteLink,
  useConnectGroup,
  useDemoGroup,
  useFindGroup,
  useJoinInviteLink,
  useKeepSyncPerson,
  useLeaveGroup,
  useLinkSwimmers,
  useSetSyncRoles,
  useSyncFamilies,
  useSyncNow,
  useSyncPeople,
  useWaitingGroup,
  useWhatsAppSync,
} from "@teamna/web-client/hooks/use-whatsapp-sync.ts";

/**
 * The WhatsApp group sync tool (strategy repo: teamna/strategy/whatsapp-group-sync.md; the screens
 * signed off in teamna/prototypes/whatsapp-sync.html on 2026-10-09). One body, in two frames: the
 * Tools page (`OwnerWhatsAppSyncScreen` in screen.tsx) and the panel that the Members page and the
 * setup import step open (`WhatsAppSyncButton`; the Staff page header lost it on 2026-11-03). There
 * is no second copy of any part of it, so a change here shows in all three places.
 *
 * Four tabs, the sort in two steps:
 *
 *   - Groups: connect a group (Teamna stays in it), sync again, remove Teamna.
 *   - Unsorted (step 1): one row per person, with the full number. The owner ticks parent, swimmer,
 *     coach (any mix), then Save or Skip.
 *   - Link swimmers (step 2): one row per parent from step 1. The owner finds the swimmers in a
 *     search, or adds new ones, then Done or Later.
 *   - Left the group: people in none of the synced groups now. Keep or Archive.
 *
 * A row the owner acted on stays on the screen until the tool closes: green when done, grey when
 * put aside, with what happened and a Change button that opens it again. The tab counts are the
 * work still to do, and when a step is empty a notice opens the next step that has work.
 */

type Tab = "groups" | "unsorted" | "families" | "left";

export function WhatsAppSyncBody({ businessId, initialTab }: { businessId: string; initialTab?: Tab }) {
  // One tool per business (internal review F6.1): the rows that stay, the drafts and the open rows
  // all belong to the business they came from. Another business is a new tool with nothing in it,
  // so no person or number of the first business shows in the second.
  return <SyncTool key={businessId} businessId={businessId} initialTab={initialTab} />;
}

function SyncTool({ businessId, initialTab }: { businessId: string; initialTab?: Tab }) {
  const state = useWhatsAppSync(businessId);
  const unsorted = useSyncPeople(businessId, "unsorted");
  const left = useSyncPeople(businessId, "left");
  const families = useSyncFamilies(businessId);
  const familyItems = useMemo(() => families.data?.items.map((item) => ({ ...item, id: item.person.id })), [families.data]);
  const sortRows = useStickyRows(unsorted.data);
  const leftRows = useStickyRows(left.data);
  const familyRows = useStickyRows(familyItems);
  const [tab, setTab] = useState<Tab | null>(initialTab ?? null);
  const { label } = useLabels();
  const [said, say] = useState("");

  if (state.isLoading) return <SkeletonList />;
  if (state.error || !state.data) return <InlineError error={state.error} />;
  const data = state.data;
  const counts = data.counts;
  const current: Tab = tab ?? (counts.unsorted > 0 ? "unsorted" : counts.families > 0 ? "families" : "groups");
  const swimmers = label("participant", { plural: true });
  const words: StepWords = { swimmers, linkTab: `Link ${swimmers}` };
  const open = (next: Tab) => setTab(next);

  return (
    <div className="flex flex-col gap-5">
      <Tabs
        label="WhatsApp sync"
        selectedId={current}
        onSelect={(id) => setTab(id as Tab)}
        tabs={[
          { id: "groups", label: "Groups", count: data.groups.length },
          { id: "unsorted", label: "Unsorted", count: counts.unsorted, todo: true },
          { id: "families", label: words.linkTab, count: counts.families, todo: true },
          { id: "left", label: "Left the group", count: counts.left, todo: true },
        ]}
      />
      {current === "groups" ? (
        <GroupsTab businessId={businessId} data={data} onSynced={() => setTab("unsorted")} onStay={() => setTab("groups")} />
      ) : null}
      {current === "unsorted" ? (
        <UnsortedTab
          businessId={businessId}
          rows={sortRows.rows}
          loading={unsorted.isLoading}
          error={unsorted.error}
          put={sortRows.put}
          waitingParents={new Set((familyItems ?? []).filter((item) => item.pending).map((item) => item.id))}
          next={counts.unsorted === 0 ? <NextStep here="unsorted" counts={counts} words={words} onOpen={open} /> : null}
          onOpen={open}
          say={say}
          words={words}
        />
      ) : null}
      {current === "families" ? (
        <FamiliesTab
          businessId={businessId}
          rows={familyRows.rows}
          swimmers={families.data?.swimmers ?? []}
          loading={families.isLoading}
          error={families.error}
          put={familyRows.put}
          next={counts.families === 0 ? <NextStep here="families" counts={counts} words={words} onOpen={open} /> : null}
          say={say}
          words={words}
        />
      ) : null}
      {current === "left" ? (
        <LeftTab
          businessId={businessId}
          rows={leftRows.rows}
          loading={left.isLoading}
          error={left.error}
          put={leftRows.put}
          next={counts.left === 0 ? <NextStep here="left" counts={counts} words={words} onOpen={open} /> : null}
          say={say}
        />
      ) : null}
      {/* What a Save, Done or Keep did, for a screen reader: the button the person pressed is gone. */}
      <p className="sr-only" aria-live="polite">
        {said}
      </p>
    </div>
  );
}

/** The panel every page opens. */
export function WhatsAppSyncPanel({ businessId, open, onClose }: { businessId: string; open: boolean; onClose: () => void }) {
  return (
    <Panel open={open} onClose={onClose} title="WhatsApp sync" subtitle="Bring the people of your WhatsApp groups into Teamna." width="xl">
      {open ? <WhatsAppSyncBody businessId={businessId} /> : null}
    </Panel>
  );
}

/** A button that opens the panel. Members, Team and the setup import step use it. */
export function WhatsAppSyncButton({
  businessId,
  label = "Sync from WhatsApp",
  variant = "secondary",
}: {
  businessId: string;
  label?: string;
  variant?: "primary" | "secondary";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <WhatsAppSyncPanel businessId={businessId} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Rows that stay
// ---------------------------------------------------------------------------

/**
 * The rows of a tab for as long as the tool is open. A row the server stops sending (the person is
 * sorted now) stays where it was; `put` replaces a row with the answer of a Save, Done or Keep. New
 * rows from a later sync go at the end, so nothing jumps under the owner's pointer.
 */
function useStickyRows<Row extends { id: string }>(server: readonly Row[] | undefined): { rows: Row[]; put: (row: Row) => void } {
  const [last, setLast] = useState<readonly Row[] | undefined>(undefined);
  const [order, setOrder] = useState<string[]>([]);
  const [seen, setSeen] = useState<Record<string, Row>>({});
  const [answers, setAnswers] = useState<Record<string, Row>>({});
  // A new answer from the server: taken in during render (React's "state from props" pattern), so
  // the rows never show one frame without the new people.
  if (server && server !== last) {
    setLast(server);
    setSeen((before) => ({ ...before, ...Object.fromEntries(server.map((row) => [row.id, row])) }));
    setOrder((before) => [...before, ...server.map((row) => row.id).filter((id) => !before.includes(id))]);
  }
  const rows = order.flatMap((id) => {
    const row = answers[id] ?? seen[id];
    return row ? [row] : [];
  });
  return { rows, put: (row) => setAnswers((before) => ({ ...before, [row.id]: row })) };
}

/** Moves the focus to a row's Change button once it shows: the button the person pressed is gone. */
function focusChange(id: string): void {
  requestAnimationFrame(() => document.getElementById(changeId(id))?.focus());
}

const changeId = (id: string) => `sync-change-${id}`;

type StepWords = { swimmers: string; linkTab: string };

/**
 * When a step has nobody left (its count from the server is 0): it says so, and opens the next step
 * that has work. It shows above the rows that stay, and alone when the step had nobody to begin with.
 */
function NextStep({
  here,
  counts,
  words,
  onOpen,
}: {
  here: Exclude<Tab, "groups">;
  counts: WhatsAppSync["counts"];
  words: StepWords;
  onOpen: (tab: Tab) => void;
}) {
  const title = { unsorted: "Everyone is sorted.", families: "Every parent is done.", left: "Nobody left to check." }[here];
  let text = "All done. New people show here after the next sync.";
  let next: { tab: Tab; label: string } | null = null;
  if (counts.unsorted > 0 && here !== "unsorted") {
    text = `Next: sort ${counts.unsorted} ${counts.unsorted === 1 ? "person" : "people"}.`;
    next = { tab: "unsorted", label: "Open Unsorted" };
  } else if (counts.families > 0 && here !== "families") {
    text = `Next: add the ${words.swimmers} of ${counts.families} ${counts.families === 1 ? "parent" : "parents"}.`;
    next = { tab: "families", label: `Add ${words.swimmers}` };
  } else if (counts.left > 0 && here !== "left") {
    text = `Next: check ${counts.left} ${counts.left === 1 ? "person who" : "people who"} left the group.`;
    next = { tab: "left", label: "Open Left the group" };
  }
  return (
    <Notice
      tone={next ? "info" : "success"}
      title={title}
      action={
        next ? (
          <Button size="sm" onClick={() => onOpen(next.tab)}>
            {next.label}
          </Button>
        ) : undefined
      }
    >
      {text}
    </Notice>
  );
}

/** The person in a row: the face, the name, the full number with Copy, and their groups. */
function PersonCell({ person, showGroups = true }: { person: SyncPerson; showGroups?: boolean }) {
  const number = formatPhone(person.phoneE164);
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={person.name} photoUrl={person.photoUrl} size={32} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[14px] font-semibold text-ink" dir="auto">
          {person.name}
        </span>
        {person.name !== number ? (
          <span className="flex items-center gap-1.5 text-[12.5px] text-ink-2">
            <span className="whitespace-nowrap tabular-nums select-all">{number}</span>
            <CopyNumber value={person.phoneE164} />
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[12.5px] text-ink-2">
            No WhatsApp name
            <CopyNumber value={person.phoneE164} />
          </span>
        )}
        {showGroups && person.groups.length ? (
          <span className="truncate text-[12px] text-ink-2" title={person.groups.join(", ")}>
            {person.groups.join(", ")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CopyNumber({ value }: { value: string }) {
  return (
    <button
      type="button"
      onClick={() => void copyText(value, "Number copied")}
      aria-label={`Copy ${formatPhone(value)}`}
      title="Copy the number"
      className="inline-flex size-6 items-center justify-center rounded-md border border-line text-ink-2 hover:border-accent-soft hover:text-accent-strong focus-visible:outline-3 focus-visible:outline-accent-soft"
    >
      <svg aria-hidden="true" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="5" y="5" width="9" height="9" rx="2" />
        <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
      </svg>
    </button>
  );
}

async function copyText(value: string, done: string): Promise<void> {
  try {
    if (!navigator.clipboard) throw new Error("No clipboard");
    await navigator.clipboard.writeText(value);
    showToast(done);
  } catch {
    showToast("Select the number and copy it");
  }
}

/** The words of a role in this business: "Parent", "Swimmer", "Coach", or the business's own words. */
function useRoleWords(): Record<SyncRole, string> {
  const { label } = useLabels();
  // A business where the one who pays also trains (pilates: "client" for both) still needs two
  // different boxes: the one who pays for others is then "Parent".
  const member = label("member", { capital: true });
  const participant = label("participant", { capital: true });
  return { parent: member === participant ? "Parent" : member, swimmer: participant, coach: label("staff", { capital: true }) };
}

const ROLE_ORDER: readonly SyncRole[] = ["parent", "swimmer", "coach"];

// ---------------------------------------------------------------------------
// Step 1: Unsorted
// ---------------------------------------------------------------------------

type SortDraft = { roles: SyncRole[]; name: string };

/** What the columns of step 1 need from the tab. */
type SortColumns = {
  businessId: string;
  roleWords: Record<SyncRole, string>;
  words: StepWords;
  waitingParents: ReadonlySet<string>;
  isDone: (row: SyncPerson) => boolean;
  isEditing: (row: SyncPerson) => boolean;
  noName: (row: SyncPerson) => boolean;
  draftOf: (row: SyncPerson) => SortDraft;
  setDraft: (row: SyncPerson, patch: Partial<SortDraft>) => void;
  onOpen: (tab: Tab) => void;
  onChange: (row: SyncPerson) => void;
  onCancel: (row: SyncPerson) => void;
  onSaved: (row: SyncPerson, person: SyncPerson) => void;
};

/** The columns of step 1. Outside the component, so no cell is a new component on each render. */
function sortColumns({
  businessId,
  roleWords,
  words,
  waitingParents,
  isDone,
  isEditing,
  noName,
  draftOf,
  setDraft,
  onOpen,
  onChange,
  onCancel,
  onSaved,
}: SortColumns): DataTableColumn<SyncPerson>[] {
  return [
    { id: "person", header: "Person", width: "30%", cell: (row) => <PersonCell person={row} /> },
    {
      id: "name",
      header: "Name",
      width: "18%",
      hideOnPhone: true,
      cell: (row) =>
        isDone(row) ? (
          <span className="text-[14px] text-ink" dir="auto">
            {noName(row) ? <span className="text-ink-2">No name</span> : row.name}
          </span>
        ) : (
          <CellField
            label={`Name of ${row.name}`}
            value={draftOf(row).name}
            placeholder={noName(row) ? formatPhone(row.phoneE164) : undefined}
            autoComplete="off"
            dir="auto"
            onChange={(event) => setDraft(row, { name: event.target.value })}
          />
        ),
    },
    {
      id: "roles",
      header: "This person is a",
      width: "34%",
      cell: (row) =>
        isDone(row) ? (
          row.classification === "skip" ? (
            <span className="text-[13px] text-ink-2">Not added to Teamna.</span>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {ROLE_ORDER.filter((role) => row.roles.includes(role)).map((role) => (
                <Badge key={role}>{roleWords[role]}</Badge>
              ))}
              {row.roles.includes("parent") && waitingParents.has(row.id) ? (
                <button type="button" onClick={() => onOpen("families")} className="text-[13px] font-semibold text-accent-strong underline underline-offset-2">
                  Add {words.swimmers}
                </button>
              ) : null}
            </div>
          )
        ) : (
          <fieldset className="flex flex-wrap gap-x-4">
            <legend className="sr-only">{row.name} is a</legend>
            {ROLE_ORDER.map((role) => (
              <Checkbox
                key={role}
                size="compact"
                label={roleWords[role]}
                checked={draftOf(row).roles.includes(role)}
                onChange={(on) => {
                  const roles = draftOf(row).roles;
                  setDraft(row, { roles: on ? [...roles, role] : roles.filter((value) => value !== role) });
                }}
              />
            ))}
          </fieldset>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "end",
      width: "18%",
      cell: (row) => (
        <SortActions
          businessId={businessId}
          row={row}
          done={isDone(row)}
          reopened={row.classification !== "unsorted" && isEditing(row)}
          draft={draftOf(row)}
          onChange={() => onChange(row)}
          onCancel={() => onCancel(row)}
          onSaved={(person) => onSaved(row, person)}
        />
      ),
    },
  ];

}

function UnsortedTab({
  businessId,
  rows,
  loading,
  error,
  put,
  waitingParents,
  next,
  onOpen,
  say,
  words,
}: {
  businessId: string;
  rows: SyncPerson[];
  loading: boolean;
  error: unknown;
  put: (row: SyncPerson) => void;
  waitingParents: ReadonlySet<string>;
  next: ReactNode;
  onOpen: (tab: Tab) => void;
  say: (text: string) => void;
  words: StepWords;
}) {
  const roleWords = useRoleWords();
  const [drafts, setDrafts] = useState<Record<string, SortDraft>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  if (error) return <InlineError error={error} />;
  if (!loading && rows.length === 0) return <>{next}</>;

  const isDone = (row: SyncPerson) => row.classification !== "unsorted" && !editing[row.id];
  const noName = (row: SyncPerson) => row.name === formatPhone(row.phoneE164);
  const draftOf = (row: SyncPerson): SortDraft =>
    drafts[row.id] ?? { roles: row.classification === "unsorted" || row.classification === "skip" ? [] : [...row.roles], name: noName(row) ? "" : row.name };
  const setDraft = (row: SyncPerson, patch: Partial<SortDraft>) => setDrafts((before) => ({ ...before, [row.id]: { ...draftOf(row), ...patch } }));

  const columns = sortColumns({
    businessId,
    roleWords,
    words,
    waitingParents,
    isDone,
    isEditing: (row) => Boolean(editing[row.id]),
    noName,
    draftOf,
    setDraft,
    onOpen,
    onChange: (row) => {
      setDrafts((before) => {
        const { [row.id]: _gone, ...rest } = before;
        return rest;
      });
      setEditing((before) => ({ ...before, [row.id]: true }));
    },
    onCancel: (row) => {
      setEditing((before) => ({ ...before, [row.id]: false }));
      focusChange(row.id);
    },
    onSaved: (row, person) => {
      put(person);
      setEditing((before) => ({ ...before, [row.id]: false }));
      setDrafts((before) => {
        const { [row.id]: _gone, ...rest } = before;
        return rest;
      });
      say(person.classification === "skip" ? `${person.name} skipped` : `${person.name} saved`);
      focusChange(row.id);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {next}
      <p className="text-[14px] text-ink-2">
        <StepChip>Step 1 of 2</StepChip> Choose who each person is. A person can be more than one. Nobody is told, and nobody is
        invited here. You add the {words.swimmers} of each parent in {words.linkTab}.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowTone={(row) => (isDone(row) ? (row.classification === "skip" ? "aside" : "done") : undefined)}
        isLoading={loading}
        density="compact"
        caption="People to sort"
        empty={{ title: "Everyone is sorted", message: "New people from your groups show here after a sync." }}
      />
    </div>
  );
}

function StepChip({ children }: { children: ReactNode }) {
  return <span className="mr-1 rounded-md bg-accent-surface px-1.5 py-0.5 text-[11px] font-bold tracking-[0.05em] text-accent-strong uppercase">{children}</span>;
}

function SortActions({
  businessId,
  row,
  done,
  reopened,
  draft,
  onChange,
  onCancel,
  onSaved,
}: {
  businessId: string;
  row: SyncPerson;
  done: boolean;
  reopened: boolean;
  draft: SortDraft;
  onChange: () => void;
  onCancel: () => void;
  onSaved: (person: SyncPerson) => void;
}) {
  const save = useSetSyncRoles(businessId);
  if (done) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {row.classification === "skip" ? <Badge>Skipped</Badge> : <Badge tone="confirmed">Saved</Badge>}
        <Button id={changeId(row.id)} variant="ghost" size="sm" onClick={onChange}>
          Change
        </Button>
      </div>
    );
  }
  const typed = draft.name.trim();
  const name = typed && typed !== row.name ? { name: typed } : {};
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={save.isPending && save.variables.roles.length > 0}
          disabled={draft.roles.length === 0 || save.isPending}
          onClick={() => save.mutate({ id: row.id, roles: draft.roles, ...name }, { onSuccess: onSaved })}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          loading={save.isPending && save.variables.roles.length === 0}
          disabled={save.isPending}
          onClick={() => save.mutate({ id: row.id, roles: [], ...name }, { onSuccess: onSaved })}
        >
          Skip
        </Button>
        {reopened ? (
          <Button variant="ghost" size="sm" disabled={save.isPending} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
      {save.error ? <InlineError error={save.error} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Link swimmers
// ---------------------------------------------------------------------------

type FamilyRow = SyncFamily & { id: string };
type NewSwimmer = { key: number; name: string; age: number | null };
type Staged = { ids: string[]; fresh: NewSwimmer[] };
let newSwimmerKey = 0;

const lastWord = (text: string) => text.trim().split(/\s+/).at(-1)?.toLowerCase() ?? "";

function FamiliesTab({
  businessId,
  rows,
  swimmers,
  loading,
  error,
  put,
  next,
  say,
  words,
}: {
  businessId: string;
  rows: FamilyRow[];
  swimmers: SyncSwimmer[];
  loading: boolean;
  error: unknown;
  put: (row: FamilyRow) => void;
  next: ReactNode;
  say: (text: string) => void;
  words: StepWords;
}) {
  const { label } = useLabels();
  const swimmer = label("participant");
  const [staged, setStaged] = useState<Record<string, Staged>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [outcome, setOutcome] = useState<Record<string, "linked" | "later">>({});
  if (error) return <InlineError error={error} />;
  if (!loading && rows.length === 0) return <>{next}</>;
  const isDone = (row: FamilyRow) => !row.pending && !editing[row.id];
  const outcomeOf = (row: FamilyRow) => outcome[row.id] ?? (row.swimmers.length ? "linked" : "later");
  const empty: Staged = { ids: [], fresh: [] };

  const columns = familyColumns({
    businessId,
    swimmers,
    swimmer,
    words,
    isDone,
    isEditing: (row) => Boolean(editing[row.id]),
    outcomeOf,
    stagedOf: (row) => staged[row.id] ?? empty,
    setStaged: (row, value) => setStaged((before) => ({ ...before, [row.id]: value })),
    onChange: (row) => setEditing((before) => ({ ...before, [row.id]: true })),
    onCancel: (row) => {
      setEditing((before) => ({ ...before, [row.id]: false }));
      setStaged((before) => ({ ...before, [row.id]: empty }));
      focusChange(row.id);
    },
    onSaved: (row, family, later) => {
      put({ ...family, id: row.id });
      setOutcome((before) => ({ ...before, [row.id]: later ? "later" : "linked" }));
      setEditing((before) => ({ ...before, [row.id]: false }));
      setStaged((before) => ({ ...before, [row.id]: empty }));
      const count = family.swimmers.length;
      say(later ? `${row.person.name}: later` : `${row.person.name}: ${count} ${count === 1 ? swimmer : words.swimmers} linked`);
      focusChange(row.id);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {next}
      <p className="text-[14px] text-ink-2">
        <StepChip>Step 2 of 2</StepChip> Add the {words.swimmers} of each parent. Find a {swimmer} who is in Teamna already, or type a
        new name. A {swimmer} can have two parents. Choose Later if you do not know yet.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowTone={(row) => (isDone(row) ? (outcomeOf(row) === "linked" ? "done" : "aside") : undefined)}
        isLoading={loading}
        density="compact"
        caption={`Parents and their ${words.swimmers}`}
        empty={{ title: "Every parent is done", message: "Parents you save in Unsorted show here." }}
      />
    </div>
  );
}

/** What the columns of step 2 need from the tab. */
type FamilyColumns = {
  businessId: string;
  swimmers: SyncSwimmer[];
  /** The business's word for one swimmer: "swimmer", "client". */
  swimmer: string;
  words: StepWords;
  isDone: (row: FamilyRow) => boolean;
  isEditing: (row: FamilyRow) => boolean;
  outcomeOf: (row: FamilyRow) => "linked" | "later";
  stagedOf: (row: FamilyRow) => Staged;
  setStaged: (row: FamilyRow, value: Staged) => void;
  onChange: (row: FamilyRow) => void;
  onCancel: (row: FamilyRow) => void;
  onSaved: (row: FamilyRow, family: SyncFamily, later: boolean) => void;
};

/** The columns of step 2. Outside the component, so no cell is a new component on each render. */
function familyColumns(ctx: FamilyColumns): DataTableColumn<FamilyRow>[] {
  return [
    { id: "parent", header: "Parent", width: "34%", cell: (row) => <PersonCell person={row.person} /> },
    {
      id: "swimmers",
      header: ctx.words.swimmers,
      width: "46%",
      cell: (row) => (
        <SwimmersCell
          row={row}
          swimmers={ctx.swimmers}
          swimmer={ctx.swimmer}
          words={ctx.words}
          done={ctx.isDone(row)}
          staged={ctx.stagedOf(row)}
          onStage={(value) => ctx.setStaged(row, value)}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "end",
      width: "20%",
      cell: (row) => (
        <LinkActions
          businessId={ctx.businessId}
          row={row}
          done={ctx.isDone(row)}
          reopened={!row.pending && ctx.isEditing(row)}
          outcome={ctx.outcomeOf(row)}
          staged={ctx.stagedOf(row)}
          onChange={() => ctx.onChange(row)}
          onCancel={() => ctx.onCancel(row)}
          onSaved={(family, later) => ctx.onSaved(row, family, later)}
        />
      ),
    },
  ];
}

/** The search list of one parent: same family name first, then the rest; the ones chosen are marked. */
function swimmerOptions(row: FamilyRow, swimmers: readonly SyncSwimmer[], staged: Staged): ComboboxOption[] {
  const own = new Set([...row.swimmers.map((item) => item.participantId), ...staged.ids]);
  const family = row.person.name === formatPhone(row.person.phoneE164) ? "" : lastWord(row.person.name);
  const same = (item: SyncSwimmer) => family !== "" && (lastWord(item.name) === family || lastWord(item.familyName) === family);
  const toOption = (item: SyncSwimmer, group: string): ComboboxOption => ({
    id: item.participantId,
    label: item.name,
    detail: `${item.age !== null ? `${item.age}, ` : ""}family of ${item.familyName}`,
    group,
    added: own.has(item.participantId),
  });
  return [
    ...swimmers.filter(same).map((item) => toOption(item, "Same family name")),
    ...swimmers.filter((item) => !same(item)).map((item) => toOption(item, "Already in Teamna")),
  ];
}

/**
 * The swimmers of one parent: the ones linked already, the ones chosen now (with a remove button),
 * the search, and the short form for a new swimmer (name and age).
 */
function SwimmersCell({
  row,
  swimmers,
  swimmer,
  words,
  done,
  staged,
  onStage,
}: {
  row: FamilyRow;
  swimmers: SyncSwimmer[];
  swimmer: string;
  words: StepWords;
  done: boolean;
  staged: Staged;
  onStage: (value: Staged) => void;
}) {
  const [draft, setDraft] = useState<{ name: string; age: string } | null>(null);
  const byId = new Map(swimmers.map((item) => [item.participantId, item]));
  const linked = row.swimmers.map((item) => <SwimmerChip key={item.participantId} name={item.name} age={item.age} />);

  if (done) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {linked.length ? linked : <span className="text-[13px] text-ink-2">No {words.swimmers} yet. Add them later on the Families page.</span>}
      </div>
    );
  }

  function addNew() {
    if (!draft?.name.trim()) {
      showToast(`Type the ${swimmer}'s name`);
      document.getElementById(`new-swimmer-${row.id}`)?.focus();
      return;
    }
    const age = Number.parseInt(draft.age, 10);
    onStage({ ...staged, fresh: [...staged.fresh, { key: (newSwimmerKey += 1), name: draft.name.trim(), age: Number.isFinite(age) ? age : null }] });
    setDraft(null);
    requestAnimationFrame(() => (document.querySelector(`[data-swimmer-search="${row.id}"] input`) as HTMLInputElement | null)?.focus());
  }

  const chosen = [
    ...staged.ids.flatMap((id) => {
      const item = byId.get(id);
      return item
        ? [<SwimmerChip key={id} name={item.name} age={item.age} onRemove={() => onStage({ ...staged, ids: staged.ids.filter((value) => value !== id) })} />]
        : [];
    }),
    ...staged.fresh.map((item) => (
      <SwimmerChip
        key={`new-${item.key}`}
        name={item.name}
        age={item.age}
        fresh
        onRemove={() => onStage({ ...staged, fresh: staged.fresh.filter((value) => value.key !== item.key) })}
      />
    )),
  ];

  return (
    <div className="flex flex-col gap-2">
      {linked.length || chosen.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {linked}
          {chosen}
        </div>
      ) : null}
      <div data-swimmer-search={row.id}>
        <Combobox
          label={`Find a ${swimmer} for ${row.person.name}`}
          placeholder={`Find a ${swimmer}, or type a new name`}
          options={swimmerOptions(row, swimmers, staged)}
          searchBox="whatsapp_sync"
          onPick={(option) => onStage({ ...staged, ids: [...staged.ids, option.id] })}
          onCreate={(typed) => {
            setDraft({ name: typed, age: "" });
            requestAnimationFrame(() => document.getElementById(typed ? `new-swimmer-age-${row.id}` : `new-swimmer-${row.id}`)?.focus());
          }}
          createLabel={(typed) => (typed ? `New ${swimmer}: ${typed}` : `New ${swimmer} (name and age)`)}
        />
      </div>
      {draft ? (
        // A small form, so Enter in either box adds the swimmer.
        <form
          className="flex flex-wrap items-center gap-2"
          aria-label={`New ${swimmer} for ${row.person.name}`}
          onSubmit={(event) => {
            event.preventDefault();
            addNew();
          }}
        >
          <div className="min-w-0 flex-1 basis-40">
            <CellField
              id={`new-swimmer-${row.id}`}
              label={`New ${swimmer} name`}
              placeholder={`${swimmer.charAt(0).toUpperCase()}${swimmer.slice(1)} name`}
              autoComplete="off"
              dir="auto"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>
          <div className="w-20">
            <CellField
              id={`new-swimmer-age-${row.id}`}
              label={`Age of the new ${swimmer}`}
              placeholder="Age"
              inputMode="numeric"
              value={draft.age}
              onChange={(event) => setDraft({ ...draft, age: event.target.value.replace(/\D/g, "").slice(0, 2) })}
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Add
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
            Cancel
          </Button>
        </form>
      ) : null}
    </div>
  );
}

function SwimmerChip({ name, age, fresh = false, onRemove }: { name: string; age: number | null; fresh?: boolean; onRemove?: () => void }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-pill bg-accent-surface py-0.5 pl-2.5 text-[12.5px] font-semibold text-accent-strong ${onRemove ? "pr-0.5" : "pr-2.5"}`}>
      <span dir="auto">{name}</span>
      {age !== null ? <span className="font-medium text-ink-2">{age}</span> : null}
      {fresh ? <span className="font-medium text-ink-2">(new)</span> : null}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          className="inline-flex size-5 items-center justify-center rounded-full text-[14px] leading-none hover:bg-paper focus-visible:outline-3 focus-visible:outline-accent-soft"
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

function LinkActions({
  businessId,
  row,
  done,
  reopened,
  outcome,
  staged,
  onChange,
  onCancel,
  onSaved,
}: {
  businessId: string;
  row: FamilyRow;
  done: boolean;
  reopened: boolean;
  outcome: "linked" | "later";
  staged: Staged;
  onChange: () => void;
  onCancel: () => void;
  onSaved: (family: SyncFamily, later: boolean) => void;
}) {
  const link = useLinkSwimmers(businessId);
  // One key per Done of this parent, kept until it succeeds: a retry replays it (no swimmer twice).
  const key = useRef(newIdempotencyKey());
  if (done) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {outcome === "linked" ? <Badge tone="confirmed">Linked</Badge> : <Badge>Later</Badge>}
        <Button id={changeId(row.id)} variant="ghost" size="sm" onClick={onChange}>
          Change
        </Button>
      </div>
    );
  }
  const nothing = staged.ids.length === 0 && staged.fresh.length === 0;
  function send(later: boolean) {
    link.mutate(
      {
        id: row.id,
        idempotencyKey: key.current,
        // Later is "not now": the swimmers picked so far are dropped, never added (internal review F1.2).
        ...(later
          ? { participantIds: [], children: [], later: true }
          : { participantIds: staged.ids, children: staged.fresh.map((item) => ({ name: item.name, age: item.age })) }),
      },
      {
        onSuccess: (family) => {
          key.current = newIdempotencyKey();
          onSaved(family, later);
        },
      },
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={link.isPending && !link.variables.later}
          disabled={(nothing && row.swimmers.length === 0) || link.isPending}
          onClick={() => send(false)}
        >
          Done
        </Button>
        <Button variant="ghost" size="sm" loading={link.isPending && Boolean(link.variables.later)} disabled={link.isPending} onClick={() => send(true)}>
          Later
        </Button>
        {reopened ? (
          <Button variant="ghost" size="sm" disabled={link.isPending} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
      {link.error ? <InlineError error={link.error} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left the group
// ---------------------------------------------------------------------------

function LeftTab({
  businessId,
  rows,
  loading,
  error,
  put,
  next,
  say,
}: {
  businessId: string;
  rows: SyncPerson[];
  loading: boolean;
  error: unknown;
  put: (row: SyncPerson) => void;
  next: ReactNode;
  say: (text: string) => void;
}) {
  const { business } = useActiveBusiness();
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  if (error) return <InlineError error={error} />;
  if (!loading && rows.length === 0) return <>{next}</>;
  const status = (row: SyncPerson): "kept" | "archived" | null => (row.canRestore ? "archived" : row.keptAt ? "kept" : null);
  const isDone = (row: SyncPerson) => status(row) !== null && !editing[row.id];

  const columns = leftColumns({
    businessId,
    timezone: business?.timezone,
    status,
    isDone,
    onChange: (row) => setEditing((before) => ({ ...before, [row.id]: true })),
    onCancel: (row) => {
      setEditing((before) => ({ ...before, [row.id]: false }));
      focusChange(row.id);
    },
    onSaved: (row, person) => {
      // Keep and Archive clear the "left" mark on the server; the row still says when they left.
      put({ ...person, leftGroupAt: person.leftGroupAt ?? row.leftGroupAt });
      setEditing((before) => ({ ...before, [row.id]: false }));
      say(person.canRestore ? `${person.name} archived` : `${person.name} kept`);
      focusChange(row.id);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {next}
      <p className="text-[14px] text-ink-2">
        These people are in none of your synced groups now. Keep them if they still come. Teamna never archives a person by itself.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowTone={(row) => (isDone(row) ? (status(row) === "kept" ? "done" : "aside") : undefined)}
        isLoading={loading}
        density="compact"
        caption="People who left the groups"
        empty={{ title: "Nobody left", message: "People who are in none of your synced groups any more show here." }}
      />
    </div>
  );
}

/** The columns of Left the group. Outside the component, so no cell is a new component on each render. */
function leftColumns(ctx: {
  businessId: string;
  timezone: string | undefined;
  status: (row: SyncPerson) => "kept" | "archived" | null;
  isDone: (row: SyncPerson) => boolean;
  onChange: (row: SyncPerson) => void;
  onCancel: (row: SyncPerson) => void;
  onSaved: (row: SyncPerson, person: SyncPerson) => void;
}): DataTableColumn<SyncPerson>[] {
  return [
    { id: "person", header: "Person", width: "52%", cell: (row) => <PersonCell person={row} showGroups={false} /> },
    {
      id: "left",
      header: "Left on",
      width: "18%",
      hideOnPhone: true,
      cell: (row) => <span className="text-[13px] text-ink-2 tabular-nums">{row.leftGroupAt ? formatDate(row.leftGroupAt, ctx.timezone) : ""}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      align: "end",
      width: "30%",
      cell: (row) => (
        <LeftActions
          businessId={ctx.businessId}
          row={row}
          status={ctx.status(row)}
          done={ctx.isDone(row)}
          onChange={() => ctx.onChange(row)}
          onCancel={() => ctx.onCancel(row)}
          onSaved={(person) => ctx.onSaved(row, person)}
        />
      ),
    },
  ];
}

function LeftActions({
  businessId,
  row,
  status,
  done,
  onChange,
  onCancel,
  onSaved,
}: {
  businessId: string;
  row: SyncPerson;
  status: "kept" | "archived" | null;
  done: boolean;
  onChange: () => void;
  onCancel: () => void;
  onSaved: (person: SyncPerson) => void;
}) {
  const keep = useKeepSyncPerson(businessId);
  const archive = useArchiveSyncPerson(businessId);
  const [confirm, setConfirm] = useState(false);
  const preview = useArchivePreview(businessId, confirm ? row.id : null);
  const blocked = (preview.data?.upcomingBookings ?? 0) > 0;

  if (done) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {status === "kept" ? <Badge tone="confirmed">Kept</Badge> : <Badge>Archived</Badge>}
        <Button id={changeId(row.id)} variant="ghost" size="sm" onClick={onChange}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap justify-end gap-2">
        {status !== "kept" ? (
          <Button variant="secondary" size="sm" loading={keep.isPending} disabled={archive.isPending} onClick={() => keep.mutate(row.id, { onSuccess: onSaved })}>
            Keep
          </Button>
        ) : null}
        {status !== "archived" ? (
          <Button variant="ghost" size="sm" disabled={keep.isPending} onClick={() => setConfirm(true)}>
            Archive
          </Button>
        ) : null}
        {status !== null ? (
          <Button variant="ghost" size="sm" disabled={keep.isPending || archive.isPending} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
      {keep.error ? <InlineError error={keep.error} /> : null}
      <ConfirmDialog
        open={confirm}
        title={`Archive ${row.name}?`}
        confirmLabel="Archive"
        tone="danger"
        loading={archive.isPending}
        // Archive only after the preview says what goes, and never while sessions are booked.
        confirmDisabled={!preview.data || preview.isFetching || blocked}
        onCancel={() => setConfirm(false)}
        onConfirm={() => {
          if (!preview.data || preview.isFetching || blocked) return;
          archive.mutate(row.id, {
            onSuccess: (person) => {
              setConfirm(false);
              onSaved(person);
            },
          });
        }}
      >
        {preview.error ? <InlineError error={preview.error} /> : <ArchiveText preview={preview.data} loading={preview.isFetching} />}
        {archive.error ? <InlineError error={archive.error} /> : null}
      </ConfirmDialog>
    </div>
  );
}

function ArchiveText({
  preview,
  loading,
}: {
  preview: { roles: SyncRole[]; upcomingBookings: number; sessionsLeft: number } | undefined;
  loading: boolean;
}): ReactNode {
  if (loading || !preview) return "Checking the bookings.";
  if (preview.upcomingBookings > 0) {
    return `This person has ${preview.upcomingBookings} ${preview.upcomingBookings === 1 ? "session" : "sessions"} booked. Cancel them first.`;
  }
  // Only what this archive takes away, from the roles the person has here.
  const parts: string[] = [];
  if (preview.roles.includes("parent")) parts.push("their family goes off the list");
  if (preview.roles.includes("swimmer")) parts.push("their own place as an adult who trains stops");
  if (preview.roles.includes("coach")) parts.push("they leave the team");
  const what = parts.length ? `${parts.join("; ")}.` : "They go off your list.";
  const left = preview.sessionsLeft > 0 ? ` They have ${preview.sessionsLeft} ${preview.sessionsLeft === 1 ? "session" : "sessions"} left, which they lose.` : "";
  return `${what.charAt(0).toUpperCase()}${what.slice(1)}${left} Change, then Keep, brings it all back.`;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

/**
 * `onStay` keeps the tool on Groups while the owner connects with a link. With no tab chosen, the tool
 * opens Unsorted as soon as it has people, and the owner would miss the result and the tip.
 */
function GroupsTab({
  businessId,
  data,
  onSynced,
  onStay,
}: {
  businessId: string;
  data: WhatsAppSync;
  onSynced: () => void;
  onStay: () => void;
}) {
  if (data.mode === "off") {
    return (
      <Notice tone="info" title="WhatsApp groups are not connected on this server yet">
        Paste the numbers or use a CSV file on the import page for now.
      </Notice>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <ConnectGroup businessId={businessId} data={data} onSynced={onSynced} onStay={onStay} />
      {data.groups.length > 0 ? (
        <section aria-labelledby="synced-groups" className="flex flex-col gap-3">
          <h3 id="synced-groups" className="text-[16px]">
            Your groups
          </h3>
          <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-paper">
            {data.groups.map((group) => (
              <GroupRow key={group.id} businessId={businessId} group={group} mode={data.mode} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function GroupRow({ businessId, group, mode }: { businessId: string; group: SyncGroup; mode: WhatsAppSync["mode"] }) {
  const syncNow = useSyncNow(businessId);
  const leave = useLeaveGroup(businessId);
  const { business } = useActiveBusiness();
  const [confirmLeave, setConfirmLeave] = useState(false);
  const staying = group.stay && !group.leftAt;

  return (
    <li className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[15px] font-semibold text-ink">{group.groupName ?? "WhatsApp group"}</span>
        {staying ? <Badge tone="confirmed">Teamna stays</Badge> : <Badge tone="quiet">Teamna left</Badge>}
      </div>
      <p className="text-[14px] text-ink-2">
        {group.memberCount ?? 0} {group.memberCount === 1 ? "person" : "people"}
        {group.lastSyncedAt ? `. Last sync ${formatWhen(group.lastSyncedAt, business?.timezone)}.` : "."}
        {staying ? "" : " To sync again, add the Teamna number or send an invite link."}
      </p>
      {staying && mode !== "off" ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={syncNow.isPending}
            onClick={() =>
              syncNow.mutate(group.id, {
                onSuccess: (result) => showToast(syncSummary(result)),
              })
            }
          >
            Sync now
          </Button>
          <Button variant="ghost" onClick={() => setConfirmLeave(true)}>
            Remove Teamna from this group
          </Button>
        </div>
      ) : null}
      {syncNow.error ? <InlineError error={syncNow.error} /> : null}
      {leave.error ? <InlineError error={leave.error} /> : null}
      <ConfirmDialog
        open={confirmLeave}
        title="Remove Teamna from this group?"
        confirmLabel="Remove Teamna"
        tone="danger"
        loading={leave.isPending}
        onCancel={() => setConfirmLeave(false)}
        onConfirm={() =>
          leave.mutate(group.id, {
            onSuccess: () => {
              setConfirmLeave(false);
              showToast("Teamna left the group");
            },
          })
        }
      >
        The people stay in Teamna. Teamna does not see who joins or leaves the group after this.
      </ConfirmDialog>
    </li>
  );
}

function syncSummary(result: { added: number; updated: number; left: number }): string {
  return `${result.added} new, ${result.updated} updated, ${result.left} left the group`;
}

/** The shared Desk date and time ("Tue 2 Sep, 5:00 PM") in the business time zone (review F4.2). */
function formatWhen(iso: string, timezone: string | undefined): string {
  return formatDateTime(iso, timezone);
}

/** When the card looks for the group by itself after it opens the connect, in ms. Then "Check again" only. */
const AUTO_FIND_AFTER = [0, 45_000];

/** How the owner connects a group: add the Teamna number, or paste the group invite link (owner decision 2026-10-10). */
type ConnectMethod = "add" | "link";

/**
 * Connect a group, in one of two ways. Teamna stays in the group until the owner removes it.
 *
 *   - Add the Teamna number (first, as before): the owner adds the number from their phone; Whapi
 *     tells the API, and this card polls our API. When the number is in the group already (added
 *     before the connect was open), "find" takes it: once when the connect opens, once more a little
 *     later, then on "Check again".
 *   - Send an invite link: for when WhatsApp does not let the owner add the number (a demo on
 *     2026-10-10). `InviteLinkConnect` reads the group from the link, then joins.
 */
function ConnectGroup({
  businessId,
  data,
  onSynced,
  onStay,
}: {
  businessId: string;
  data: WhatsAppSync;
  onSynced: () => void;
  onStay: () => void;
}) {
  const connect = useConnectGroup(businessId);
  const demo = useDemoGroup(businessId);
  const find = useFindGroup(businessId);
  // A connect waiting for an invite group (a group admin must approve Teamna) opens on the link
  // choice again after a reload, so its result shows there (internal review F4.2).
  const [method, setMethod] = useState<ConnectMethod>(data.waiting?.invitePending ? "link" : "add");
  const [linkId, setLinkId] = useState<string | null>(data.waiting?.id ?? null);
  const link = useWaitingGroup(businessId, linkId);
  const announced = useRef<string | null>(null);
  const connected = link.data?.status === "connected" ? link.data : null;
  const live = data.mode === "live";
  const findGroup = find.mutate;

  // A "too old" answer to Check again: back to the start button, which opens the same link fresh
  // (internal review F4.2). Its message stays under the button.
  function resetWhenTooOld(error: unknown) {
    if (isApiError(error) && error.code === "CONFLICT") setLinkId(null);
  }

  // Only for an add: with an invite link, the owner says which group, so nothing is looked for.
  useEffect(() => {
    if (!linkId || !live || method !== "add") return;
    const timers = AUTO_FIND_AFTER.map((delay) =>
      setTimeout(
        () =>
          findGroup(linkId, {
            onError: (error) => {
              if (isApiError(error) && error.code === "CONFLICT") setLinkId(null);
            },
          }),
        delay,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [linkId, live, method, findGroup]);

  // A waiting link stops being matched after two hours. Then the card shows the start button again,
  // which opens the same link fresh (internal review F4.2).
  const waitingUntil = link.data?.status === "waiting" ? (link.data.waitingUntil ?? null) : null;
  useEffect(() => {
    if (!waitingUntil) return;
    const timer = setTimeout(() => setLinkId(null), Math.max(0, Date.parse(waitingUntil) - Date.now()));
    return () => clearTimeout(timer);
  }, [waitingUntil]);

  // An add that connected: say so, close the connect, open Unsorted.
  const addConnected = method === "add" ? connected : null;
  useEffect(() => {
    if (!addConnected || announced.current === addConnected.id) return;
    announced.current = addConnected.id;
    showToast(addConnected.groupName ? `Synced ${addConnected.groupName}` : "Group synced");
    setLinkId(null);
    onSynced();
  }, [addConnected, onSynced]);

  // A link connect that the poll saw connect (after an admin approved Teamna) keeps its link, so the
  // link card shows the result on Groups, as after a direct join (internal review F1.3). The poll
  // stops by itself once the link is connected.
  const linkConnected = method === "link" ? connected : null;
  useEffect(() => {
    if (!linkConnected || announced.current === linkConnected.id) return;
    announced.current = linkConnected.id;
    showToast(linkConnected.groupName ? `Synced ${linkConnected.groupName}` : "Group synced");
  }, [linkConnected]);

  const noAdminNumber = data.adminPhones.length === 0;
  const waiting = Boolean(linkId) && !connected;
  const adminNumbers = <span className="font-semibold text-ink tabular-nums">{data.adminPhones.map(formatPhone).join(" or ")}</span>;

  return (
    <section aria-labelledby="connect-group" className="flex flex-col gap-4 rounded-card border border-accent bg-paper p-5 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="connect-group" className="text-[16px]">
          Sync a WhatsApp group
        </h3>
        {data.mode === "simulated" ? <Badge tone="late">Demo on this server</Badge> : null}
      </div>
      <p className="text-[14px] text-ink-2">
        Teamna reads the people in the group, with the name each person set in WhatsApp. Teamna does not read the
        messages or the profile photos. You choose who each person is.
      </p>
      <RadioGroup<ConnectMethod>
        legend="How do you want to connect Teamna?"
        value={method}
        onChange={(next) => {
          // Each way starts its own connect: a link armed for an invite group is reserved for it,
          // and "I am ready to add the number" reopens the connect fresh (internal review F3.1).
          if (next !== method) setLinkId(null);
          setMethod(next);
        }}
        options={[
          { value: "add", label: "Add the Teamna number", hint: "You add the number to the group from your phone." },
          { value: "link", label: "Send an invite link", hint: "You paste the group invite link here. Teamna joins with it." },
        ]}
      />
      {noAdminNumber ? (
        <Notice tone="warning" title="Add your mobile number first">
          Teamna finds your group from the number of the group admin. Add your mobile number in{" "}
          <Link to="/owner/settings" className="font-semibold text-accent underline">
            Settings
          </Link>
          , Sign-in methods.
        </Notice>
      ) : null}
      {method === "link" ? (
        <InviteLinkConnect
          businessId={businessId}
          data={data}
          linkId={linkId}
          setLinkId={setLinkId}
          openLink={async () => (await connect.mutateAsync()).id}
          opening={connect.isPending}
          announced={announced}
          onSynced={onSynced}
          onStay={onStay}
          connectedLater={connected ? { groupName: connected.groupName } : null}
          pendingApproval={link.data?.status === "waiting" && link.data.invitePending === true}
          onStartAgain={() => setLinkId(null)}
          adminNumbers={adminNumbers}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-sunken px-4 py-3">
            <span className="text-[20px] font-semibold tabular-nums text-ink select-all">{data.teamnaNumber}</span>
            <Button variant="secondary" onClick={() => void copyText(data.teamnaNumber.replace(/\s/g, ""), "Number copied")}>
              Copy
            </Button>
          </div>
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[14px] text-ink-2">
            {/* WhatsApp's Add members list shows saved contacts only. */}
            <li>On your phone, save the number above as a contact. Name it Teamna.</li>
            <li>Open the WhatsApp group. Tap the group name. Then tap Add members.</li>
            <li>Find Teamna in your contacts and add it.</li>
          </ol>
          <p className="text-[14px] text-ink-2">
            Teamna stays in the group. While your number is a group admin, Teamna syncs again by itself when people join or
            leave. You can remove Teamna from the group at any time.
          </p>
          {noAdminNumber ? null : (
            <p className="text-[14px] text-ink-2">
              Add the number from WhatsApp on {adminNumbers}. That number must be an admin of the group.
            </p>
          )}
          <p className="text-[14px] text-ink-2">
            WhatsApp does not let you add the number?{" "}
            <button
              type="button"
              className="font-semibold text-accent underline"
              onClick={() => {
                setLinkId(null);
                setMethod("link");
              }}
            >
              Send an invite link
            </button>
          </p>
          <div aria-live="polite">
            {waiting ? (
              <div className="flex flex-col gap-3">
                <Notice tone="info" title="Waiting for the group">
                  Keep this open. The people show here about one minute after you add the number. If you added the number
                  before, tap Check again.
                </Notice>
                {live && linkId ? (
                  <Button
                    variant="secondary"
                    loading={find.isPending}
                    onClick={() =>
                      find.mutate(linkId, {
                        onSuccess: (result) => {
                          if (result.link.status === "waiting" && !result.sync) showToast("Teamna did not find the group yet");
                        },
                        onError: resetWhenTooOld,
                      })
                    }
                  >
                    Check again
                  </Button>
                ) : null}
                {data.mode === "simulated" ? (
                  <Button
                    variant="secondary"
                    loading={demo.isPending}
                    onClick={() => linkId && demo.mutate(linkId, { onSuccess: (result) => showToast(syncSummary(result)) })}
                  >
                    Use a demo list
                  </Button>
                ) : null}
              </div>
            ) : (
              <Button
                loading={connect.isPending}
                disabled={noAdminNumber}
                onClick={() => connect.mutate(undefined, { onSuccess: (created) => setLinkId(created.id) })}
              >
                I am ready to add the number
              </Button>
            )}
          </div>
          {demo.error ? <InlineError error={demo.error} /> : null}
          {find.error ? <InlineError error={find.error} /> : null}
        </>
      )}
      {connect.error ? <InlineError error={connect.error} /> : null}
      {link.error ? <InlineError error={link.error} /> : null}
    </section>
  );
}

/**
 * Send an invite link: the owner pastes the link, Teamna reads the group from it without joining
 * ("Check the link"), the owner sees the group, then "Join and sync". The link opens (or reopens)
 * the waiting connect first, the same one an add uses. The link itself is never kept.
 *
 * After a sync the card shows the counts and a tip to reset the link, and stays on Groups until the
 * owner taps Sort people. When a group admin must approve Teamna, the connect keeps waiting and the
 * poll in `ConnectGroup` sees it connect.
 */
function InviteLinkConnect({
  businessId,
  data,
  linkId,
  setLinkId,
  openLink,
  opening,
  announced,
  onSynced,
  onStay,
  connectedLater,
  pendingApproval,
  onStartAgain,
  adminNumbers,
}: {
  businessId: string;
  data: WhatsAppSync;
  linkId: string | null;
  setLinkId: (id: string | null) => void;
  openLink: () => Promise<string>;
  opening: boolean;
  announced: { current: string | null };
  onSynced: () => void;
  onStay: () => void;
  /** Set when the poll saw this connect connect after an approval. */
  connectedLater: { groupName: string | null } | null;
  /** The connect waits for its invite group, also after a reload (when this card has no join answer). */
  pendingApproval: boolean;
  onStartAgain: () => void;
  adminNumbers: ReactNode;
}) {
  const check = useCheckInviteLink(businessId);
  const join = useJoinInviteLink(businessId);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<InviteLinkCheck | null>(null);
  const [joinOutcome, setOutcome] = useState<{ kind: "synced"; groupName: string | null; sync: SyncResult | null } | { kind: "approval"; groupName: string | null } | null>(null);
  const noAdminNumber = data.adminPhones.length === 0;
  const groupLabel = (name: string | null) => name ?? "the group";
  // After an approval the poll connects the link: the same result as a direct join, with no counts.
  const outcome: typeof joinOutcome = connectedLater
    ? { kind: "synced", groupName: connectedLater.groupName ?? (joinOutcome?.groupName ?? null), sync: null }
    : (joinOutcome ?? (pendingApproval ? { kind: "approval", groupName: null } : null));

  function startAgain() {
    onStartAgain();
    setText("");
    setPreview(null);
    setOutcome(null);
    check.reset();
    join.reset();
  }

  async function onCheck() {
    if (!text.trim()) return;
    onStay();
    setPreview(null);
    let id = linkId;
    if (!id) {
      try {
        id = await openLink();
      } catch {
        return; // `connect.error` shows under the card.
      }
      setLinkId(id);
    }
    check.mutate({ linkId: id, link: text }, { onSuccess: setPreview, onError: resetWhenTooOld });
  }

  // A connect older than two hours, or one that connected meanwhile: open a fresh one on the next tap.
  function resetWhenTooOld(error: unknown) {
    if (isApiError(error) && error.code === "CONFLICT") setLinkId(null);
  }

  function onJoin() {
    if (!linkId) return;
    const id = linkId;
    join.mutate(
      { linkId: id, link: text },
      {
        onSuccess: (result) => {
          if (result.result === "approval_needed") {
            setOutcome({ kind: "approval", groupName: result.groupName });
            return;
          }
          // This card says it, not the poll's "Synced ..." toast, and it stays on Groups.
          announced.current = id;
          setLinkId(null);
          setOutcome({ kind: "synced", groupName: result.groupName, sync: result.sync });
          showToast(result.sync ? syncSummary(result.sync) : `Synced ${groupLabel(result.groupName)}`);
        },
        onError: (error) => {
          setPreview(null);
          resetWhenTooOld(error);
        },
      },
    );
  }

  if (outcome?.kind === "synced") {
    const added = outcome.sync?.added ?? null;
    return (
      <div aria-live="polite" className="flex flex-col gap-3">
        <Notice
          tone="success"
          title={`Synced ${groupLabel(outcome.groupName)}`}
          action={
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={onSynced}>
                Sort people
              </Button>
              <Button variant="ghost" onClick={startAgain}>
                Connect another group
              </Button>
            </div>
          }
        >
          {added === null
            ? "The people are in Unsorted."
            : `${added} new ${added === 1 ? "person is" : "people are"} in Unsorted.`}{" "}
          Teamna stays in the group.
        </Notice>
        <p className="text-[14px] text-ink-2">
          Tip: anybody with the link can join the group, and Teamna does not keep it. You can reset the link in WhatsApp now
          (group info, Invite via link, Reset link). Teamna stays in the group.
        </p>
      </div>
    );
  }

  if (outcome?.kind === "approval") {
    return (
      <div aria-live="polite" className="flex flex-col gap-3">
        <Notice
          tone="warning"
          title="A group admin must approve Teamna"
          action={
            // After a reload this card has no link to join with again: the poll still sees the
            // approval, and Start again opens a new connect.
            text.trim() ? (
              <Button variant="secondary" loading={join.isPending} onClick={onJoin}>
                Check again
              </Button>
            ) : (
              <Button variant="ghost" onClick={startAgain}>
                Start again
              </Button>
            )
          }
        >
          This group asks an admin to approve new members. On your phone, open {groupLabel(outcome.groupName)}, tap Pending
          requests, and approve {data.teamnaNumber}. Keep this open. The people show here about one minute after you approve.
        </Notice>
        {join.error ? <InlineError error={join.error} /> : null}
      </div>
    );
  }

  const invalid = preview?.result === "invalid" ? "This link does not work. The link is wrong, or a group admin reset it. Copy the link from the group again." : undefined;

  return (
    <div className="flex flex-col gap-4">
      {preview?.result === "ok" ? (
        <div aria-live="polite" className="flex flex-col gap-3">
          <div className="flex items-center gap-3.5 rounded-card border border-line p-4">
            {/* Initials from the words only: "Members (simulated)" is "MS", not "M(". */}
            <Avatar name={(preview.groupName ?? "WhatsApp group").replace(/[^\p{L}\p{N}\s]/gu, "")} size={48} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-[16px] font-semibold text-ink">{groupLabel(preview.groupName)}</span>
              {preview.memberCount !== null ? (
                <span className="text-[14px] text-ink-2">
                  {preview.memberCount} {preview.memberCount === 1 ? "person" : "people"}
                </span>
              ) : null}
              {preview.adminSeen ? <span className="text-[14px] font-semibold text-confirmed">Your number is an admin of this group</span> : null}
            </div>
          </div>
          <p className="text-[14px] text-ink-2">
            Is this the right group? Teamna joins it, reads the people with the name each person set in WhatsApp,
            and stays in the group. Teamna does not read the messages or the profile photos.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button loading={join.isPending} onClick={onJoin}>
              Join and sync
            </Button>
            <Button variant="ghost" disabled={join.isPending} onClick={() => setPreview(null)}>
              Not this group
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[14px] text-ink-2">
            <li>On your phone, open the WhatsApp group.</li>
            <li>Tap the group name. Then tap Invite via link (on iPhone: Invite to Group via Link).</li>
            <li>Tap Copy link.</li>
            <li>Paste the link below. On a computer, send the link to yourself on WhatsApp first, then copy it from WhatsApp Web.</li>
          </ol>
          {noAdminNumber ? null : (
            <p className="text-[14px] text-ink-2">
              Your number {adminNumbers} must be an admin of the group. Teamna joins with the number {data.teamnaNumber} and
              stays in the group.
            </p>
          )}
          {data.mode === "simulated" ? (
            <p className="text-[14px] text-ink-2">On this server, any WhatsApp group link gives the demo list.</p>
          ) : null}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void onCheck();
            }}
          >
            <div className="min-w-0 flex-[1_1_320px]">
              <Field
                label="Group invite link"
                type="text"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                placeholder="https://chat.whatsapp.com/..."
                value={text}
                error={invalid}
                onChange={(event) => {
                  setText(event.target.value);
                  if (preview) setPreview(null);
                }}
              />
            </div>
            <Button type="submit" loading={opening || check.isPending} disabled={noAdminNumber || !text.trim()}>
              Check the link
            </Button>
          </form>
          <div aria-live="polite">
            {preview?.result === "not_admin" ? (
              <Notice tone="warning" title={`Your number is not an admin of ${groupLabel(preview.groupName)}`}>
                Teamna syncs a group only when your number is a group admin. Teamna did not join. Ask a group admin to make you
                an admin in WhatsApp, then check the link again.
              </Notice>
            ) : null}
            {preview?.result === "synced_already" ? (
              <Notice tone="info" title="Teamna syncs this group already">
                {groupLabel(preview.groupName)} is in Your groups below. Tap Sync now there to read the people again.
              </Notice>
            ) : null}
          </div>
        </>
      )}
      {check.error ? <InlineError error={check.error} /> : null}
      {join.error ? <InlineError error={join.error} /> : null}
    </div>
  );
}
